import { useState, useEffect, useCallback } from 'react';
import MaintenancePage from '@/pages/MaintenancePage';
import { supabase } from '@/integrations/supabase/client';
import { LoadingAnimation } from '@/components/LoadingAnimation';

// ============================================================
// DATABASE-DRIVEN MAINTENANCE MODE WITH FAIL-CLOSED LOGIC
// ============================================================
// Primary check: Database `admin_settings` table
// Admin/owner bypass: Allowed if `allow_admins` is true
// Fail-closed: During loading, block access (don't show app)
// ============================================================

interface MaintenanceSettings {
  enabled: boolean;
  message: string;
  start_time: string | null;
  end_time: string | null;
  allow_admins: boolean;
}

const defaultSettings: MaintenanceSettings = {
  enabled: false,
  message: 'We are working on improving the service. Please try again later.',
  start_time: null,
  end_time: null,
  allow_admins: true,
};

interface MaintenanceBlockerProps {
  children: React.ReactNode;
}

/**
 * TOP-LEVEL maintenance blocker with database-driven approach:
 * 
 * 1. FETCH: Check `admin_settings` table for `maintenance_mode` setting
 * 2. BLOCK: If enabled=true, render MaintenancePage
 * 3. BYPASS: Admin/owner users can bypass if allow_admins=true
 * 4. FAIL-CLOSED: During loading, show loading state (not app)
 */
export function MaintenanceBlocker({ children }: MaintenanceBlockerProps) {
  const [isLoading, setIsLoading] = useState(true);
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [isAdminBypassing, setIsAdminBypassing] = useState(false);
  const [settings, setSettings] = useState<MaintenanceSettings>(defaultSettings);

  const checkMaintenanceStatus = useCallback(async () => {
    try {
      // 1. Fetch maintenance settings from database
      const { data: settingsData, error: settingsError } = await supabase
        .from('admin_settings')
        .select('value')
        .eq('key', 'maintenance_mode')
        .maybeSingle();

      if (settingsError) {
        console.error('Failed to fetch maintenance settings:', settingsError);
        // FAIL-CLOSED: On error, assume NOT in maintenance (safer for users)
        // But log the error for debugging
        setIsMaintenanceActive(false);
        setIsLoading(false);
        return;
      }

      const maintenanceSettings = settingsData?.value as unknown as MaintenanceSettings | null;
      const isEnabled = maintenanceSettings?.enabled === true;
      
      if (maintenanceSettings) {
        setSettings({ ...defaultSettings, ...maintenanceSettings });
      }

      // 2. If maintenance is NOT enabled, allow through immediately
      if (!isEnabled) {
        setIsMaintenanceActive(false);
        setIsLoading(false);
        return;
      }

      // 3. Maintenance IS enabled - check for admin bypass
      setIsMaintenanceActive(true);

      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user?.id) {
        // No session = no admin bypass possible
        setIsAdminBypassing(false);
        setIsLoading(false);
        return;
      }

      // Check if user is admin/owner
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', session.user.id)
        .maybeSingle();

      const isAdmin = roleData?.role === 'admin' || roleData?.role === 'owner';
      const allowAdmins = maintenanceSettings?.allow_admins !== false;
      
      setIsAdminBypassing(isAdmin && allowAdmins);
      setIsLoading(false);
    } catch (err) {
      console.error('Maintenance check failed:', err);
      // FAIL-OPEN on unexpected errors (don't lock out everyone)
      setIsMaintenanceActive(false);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkMaintenanceStatus();

    // Listen for auth changes (login/logout)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkMaintenanceStatus();
    });

    // Subscribe to realtime changes on admin_settings
    const channel = supabase
      .channel('maintenance-settings')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'admin_settings',
          filter: "key=eq.maintenance_mode"
        },
        () => {
          console.log('Maintenance settings changed, rechecking...');
          checkMaintenanceStatus();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      channel.unsubscribe();
    };
  }, [checkMaintenanceStatus]);

  // ============================================================
  // FAIL-CLOSED: Show loading while checking status
  // ============================================================
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingAnimation />
      </div>
    );
  }

  // ============================================================
  // MAINTENANCE ACTIVE: Block unless admin bypassing
  // ============================================================
  if (isMaintenanceActive) {
    if (isAdminBypassing) {
      console.log('Maintenance active but admin bypassing');
      return <>{children}</>;
    }

    console.log('Maintenance active: blocking all access');
    return <MaintenancePage settings={settings} isAdminBypassing={false} />;
  }

  // Maintenance not active - render app normally
  return <>{children}</>;
}

// Export for use in Maintenance page
export { useMaintenanceStatus } from '@/hooks/useMaintenanceStatus';